import { useState, useContext } from 'react';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
const LandingPage = () => {
  const { login } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    privacy_consent: false,
    marketing_consent: false,
    referral_code: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate consent for registration
    if (!isLogin && !formData.privacy_consent) {
      toast.error('You must accept the Privacy Policy to register');
      return;
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData, { timeout: 15000 });
      
      // Show referral warning if provided
      if (response.data.referral_warning) {
        toast.warning(response.data.referral_warning, { duration: 5000 });
      }
      
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      console.error('Login/Register error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. The server may be busy — please try again.');
      } else if (error.response) {
        toast.error(error.response.data?.detail || `Server error: ${error.response.status}`);
      } else if (error.request) {
        console.error('No response received:', error.request);
        toast.error('Unable to connect to server. Please try again in a moment.');
      } else {
        toast.error('An error occurred: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{backgroundColor: '#157a6e'}}>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Hero Section */}
        <div className="space-y-6 fade-in">
          <div className="space-y-4">
            <img 
              src="/logo-white.png" 
              alt="UK Fantasy Netball League" 
              className="h-36 lg:h-40 w-auto mb-4 mx-auto lg:mx-0"
            />
            
 
            <p className="text-lg text-white leading-relaxed text-center lg:text-left">
              Build your fantasy team for this year&apos;s Superleague competition. Pick your starting seven and your captain, and compete with your friends on the leaderboard.
            </p>
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-bold text-white text-center lg:text-left">Some Quick Rules</h3>
            <div className="space-y-3 text-sm text-white">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full mt-1.5 bg-white flex-shrink-0"></div>
                <span>Your fantasy team can contain up to two players from one NSL team</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full mt-1.5 bg-white flex-shrink-0"></div>
                <span>You&apos;ll need to select a Captain too - they will earn double points</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full mt-1.5 bg-white flex-shrink-0"></div>
                <span>The mid-season transfer window will open when announced - you can make changes to your team only once. After saving, your team is locked for the rest of the season. You can make one change per position group: Shooting (GS & GA), Midcourt (WA, C, & WD), or Defence (GD & GK)</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full mt-1.5 bg-white flex-shrink-0"></div>
                <span>Super Cup, Semi Final, Preliminary Final, and Grand Final statistics will not be used - only the 14 regular rounds will contribute towards your points total</span>
              </div>
            </div>
          </div>
        </div>

        {/* Auth Form */}
        <Card className="w-full shadow-2xl border-0 slide-in" data-testid="auth-card">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl" style={{color: '#157a6e'}}>Join the UKFNL or Login to your Account</CardTitle>
            <CardDescription>Join the UK Fantasy Netball League today, or login to your account to stay updated with your team.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                    />
                  </div>
                  
                  {/* Forgot Password Link */}
                  <div className="text-right">
                    <a 
                      href="/forgot-password"
                      className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
                    >
                      Forgot password?
                    </a>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full text-white"
                    style={{backgroundColor: '#157a6e'}}
                    disabled={loading}
                    data-testid="login-submit-button"
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="register">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
                  <div className="space-y-2">
                    <Label htmlFor="register-username">UKFNL Team Name</Label>
                    <Input
                      id="register-username"
                      data-testid="register-username-input"
                      type="text"
                      placeholder="Pick your team name - puns welcome!"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                    />
                  </div>
                  
                  {/* Referral Code (Optional) */}
                  <div className="space-y-2">
                    <Label htmlFor="referral-code" className="text-gray-700">
                      Referral Code <span className="text-gray-400 text-xs">(Optional)</span>
                    </Label>
                    <Input
                      id="referral-code"
                      type="text"
                      placeholder="ABCDE"
                      value={formData.referral_code}
                      onChange={(e) => setFormData({...formData, referral_code: e.target.value.toUpperCase()})}
                      maxLength={5}
                      className="uppercase"
                    />
                    <p className="text-xs text-gray-500">Have a referral code? Enter it here!</p>
                  </div>
                  
                  {/* Consent Checkboxes */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="privacy-consent"
                        checked={formData.privacy_consent}
                        onChange={(e) => setFormData({...formData, privacy_consent: e.target.checked})}
                        className="mt-1"
                        required
                      />
                      <label htmlFor="privacy-consent" className="text-sm text-gray-600">
                        <span className="text-red-500">*</span> By signing up to the UK Fantasy Netball League, you are accepting the terms of service and privacy notice. Your data will only be used to manage your UKFNL account and not shared with any third parties.
                      </label>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="marketing-consent"
                        checked={formData.marketing_consent}
                        onChange={(e) => setFormData({...formData, marketing_consent: e.target.checked})}
                        className="mt-1"
                      />
                      <label htmlFor="marketing-consent" className="text-sm text-gray-600">
                        I&apos;d like to receive marketing updates from the UKFNL, including prize giveaways and similar opportunities (optional)
                      </label>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full text-white"
                    style={{backgroundColor: '#157a6e'}}
                    disabled={loading}
                    data-testid="register-submit-button"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;